<template>
  <section class="w-100">
    <div
      :id="`insurance-${index}`"
      v-b-toggle="`collapse-insurance-${index}`"
      class="col pt-3 mb-0">
      <div class="row">
        <div class="col">
          <h5
            v-if="type && type !== 'INSURANCE_TYPE_UNDEFINED'"
            class="cursor-pointer hover width-only-content">
            {{ insuranceListTitle }}
          </h5>
          <h5
            v-else
            class="cursor-pointer hover width-only-content">
            {{ $t('message.onBoarding.buildings.insurances.newInsurance') }}
          </h5>
        </div>
      </div>
    </div>
    <div class="col-12 pl-0">
      <b-collapse
        :id="`collapse-insurance-${index}`"
        :ref="'collapse'"
        :visible="expandByDefault"
        class="pl-3">
        <div
          class="row details-row-bg-1 py-01rem mt-3"
          style="row-gap: .5rem;">
          <div class="col-sm-12 col-md-3">
            <label for="type">{{ $t('message.onBoarding.buildings.insurances.type.title') }}*</label>
            <coozzy-form-select
              v-if="!insuranceInputReadOnly"
              id="type"
              v-model="type"
              :state="$v.type.$dirty && $v.type.$error ? false : null">
              <option value="INSURANCE_TYPE_UNDEFINED" />
              <option
                v-for="(typeValue, typeIndex) in availableTypes"
                :key="typeIndex"
                :value="typeValue">
                {{ $t('message.onBoarding.buildings.insurances.type.' + typeValue) }}
              </option>
            </coozzy-form-select>
            <p
              v-else
              class="show-data">
              {{ !type || type === 'INSURANCE_TYPE_UNDEFINED' ? '-' : $t('message.onBoarding.buildings.insurances.type.' + type) }}
            </p>
          </div>
          <div
            v-if="syncedToAic"
            class="col-sm-12 col-md-3">
            <label>{{ $t('message.onBoarding.buildings.insurances.benefits.title') }}</label>
            <p v-if="!insurance.id">
              -
            </p>
            <a
              v-else-if="!aicLoading"
              class="link d-block"
              @click.prevent="showCoverageInAic"
              @click.middle="showCoverageInAic"
              @mousedown.middle.prevent.stop>
              {{ $t('message.onBoarding.buildings.insurances.aic.showInsuranceCoverage') }} <coozzy-external-link-icon />
            </a>
            <coozzy-spinner v-else />
          </div>
          <div
            v-else-if="insuranceInputReadOnly"
            class="col-sm-12 col-md-3">
            <label>{{ $t('message.onBoarding.buildings.insurances.benefits.title') }}</label>
            <p
              v-if="getBenefits.length > 0"
              class="mb-0 multi-select-value">
              {{ getBenefits }}
            </p>
            <p
              v-else
              class="mb-0 multi-select-value">
              -
            </p>
          </div>
          <div
            v-else
            class="col-sm-12 col-md-3">
            <coozzy-multiselect
              id="benefits"
              v-model="benefits"
              :options="translatedBenefits"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :preselect-first="false"
              :taggable="false"
              label="label"
              track-by="label">
              {{ $t('message.onBoarding.buildings.insurances.benefits.title') }}
            </coozzy-multiselect>
          </div>
          <div class="col-sm-12 col-md-3">
            <coozzy-form-input
              v-model="insuranceDate.premiumPaidUntil"
              type="date"
              :is-read-only="insuranceInputReadOnly"
              :filter="'formatDate'"
              :name="$t('message.onBoarding.buildings.insurances.premiumPaidUpTo')"
              @blur="dateToTimestamp()" />
          </div>
          <div class="col-sm-12 col-md-3">
            <label for="insuranceCompany">{{ $t('message.onBoarding.buildings.insurances.company') }}*</label>
            <coozzy-form-select
              v-if="!insuranceInputReadOnly"
              id="insuranceCompany"
              v-model="insuranceCompany"
              :disabled="isInsuranceCompanyReadOnly"
              :state="$v.insuranceCompany.$dirty && $v.insuranceCompany.$error ? false : null">
              <option value="" />
              <option
                v-for="(company, companyIndex) in insuranceCompanyOptions"
                :key="companyIndex"
                :value="company">
                {{ company }}
              </option>
            </coozzy-form-select>
            <p v-else>
              {{ insuranceCompany || '-' }}
            </p>
          </div>
          <div class="col-sm-12 col-md-3">
            <coozzy-form-input
              v-model="policyNumber"
              :is-read-only="insuranceInputReadOnly"
              :name="$t('message.onBoarding.buildings.insurances.aic.policyNumber')" />
          </div>
          <div
            v-if="isCantonalBuildingInsurance"
            class="col-sm-12 col-md-3">
            <coozzy-form-input
              v-model="indexYearInput"
              type="text"
              :is-read-only="insuranceInputReadOnly"
              :limit="4"
              :prevent-letters="true"
              :name="$t('message.onBoarding.buildings.insurances.indexYear')"
              :state="$v.indexYear.$dirty && $v.indexYear.$error ? false : null" />
          </div>
          <div class="col-sm-12 col-md-3">
            <coozzy-form-input-numeric
              v-model="amount"
              type="number"
              :is-read-only="insuranceInputReadOnly"
              :filter="'formatPrice'"
              placeholder="CHF"
              :is-decimal="true"
              :name="$t('message.onBoarding.buildings.insurances.aic.amount') + (isCantonalBuildingInsurance && isNewInsurance ? '*' : '')"
              :check-valide="$v.amount.$dirty && $v.amount.$error ? 'is-invalid' : ''" />
          </div>
          <div class="col-sm-12 col-md-3">
            <coozzy-form-input-numeric
              v-model="yearlyCosts"
              type="number"
              :is-read-only="insuranceInputReadOnly"
              :filter="'formatPrice'"
              placeholder="CHF"
              :is-decimal="true"
              :name="$t('message.onBoarding.buildings.insurances.costs')"
              :check-valide="$v.yearlyCosts.$dirty && $v.yearlyCosts.$error ? 'is-invalid' : ''" />
          </div>
          <div class="col-sm-12 col-md-3">
            <coozzy-form-input-numeric
              v-model="excess"
              type="number"
              :is-read-only="insuranceInputReadOnly"
              :filter="'formatPrice'"
              placeholder="CHF"
              :is-decimal="true"
              :name="$t('message.onBoarding.buildings.insurances.excess')"
              :check-valide="$v.excess.$dirty && $v.excess.$error ? 'is-invalid' : ''" />
          </div>
          <div class="col-sm-12 col-md-3">
            <coozzy-form-input
              v-model="insuranceDate.startDate"
              type="date"
              :is-read-only="insuranceInputReadOnly"
              :filter="'formatDate'"
              :name="$t('message.onBoarding.buildings.insurances.aic.startingAt')"
              :state="$v.insuranceDate.startDate.$dirty && $v.insuranceDate.startDate.$error ? false : null"
              @blur="dateToTimestamp()" />
          </div>
          <div class="col-sm-12 col-md-3">
            <coozzy-form-input
              v-model="insuranceDate.endDate"
              type="date"
              :is-read-only="insuranceInputReadOnly"
              :filter="'formatDate'"
              :name="$t('message.onBoarding.buildings.insurances.aic.endingAt')"
              @blur="dateToTimestamp()" />
          </div>
          <div class="col-sm-12 col-md-3">
            <coozzy-form-input
              v-model="insuranceDate.earliestTerminationDate"
              type="date"
              :is-read-only="insuranceInputReadOnly"
              :filter="'formatDate'"
              :name="$t('message.onBoarding.buildings.insurances.noticeDate')"
              :state="$v.insuranceDate.earliestTerminationDate.$dirty && $v.insuranceDate.earliestTerminationDate.$error ? false : null"
              @blur="dateToTimestamp()" />
          </div>
          <div class="col-sm-12 col-md-3">
            <coozzy-form-input
              v-model="notes"
              :is-read-only="insuranceInputReadOnly"
              :name="$t('message.onBoarding.buildings.insurances.note')" />
          </div>
        </div>
        <div class="row details-row-bg-2 py-01rem">
          <div class="col-2 mt-3">
            <add-document-modal
              :suffix="'insurances' + index"
              :owner-id="target === 'property' ? insurance.ownerId : building.ownerId"
              @document-created="documentCreated" />
            <coozzy-button
              v-if="!insuranceInputReadOnly"
              design="prop-green"
              class="w-100"
              @click="addDocumentClicked('insurances' + index)">
              {{ $t('message.manageDocuments.addDocument') }}
            </coozzy-button>
          </div>
          <div
            v-if="documents.length > 0"
            class="col-12 mt-2">
            <documents-list
              :documents-list="documents"
              :is-read-only="insuranceInputReadOnly"
              :aic-managed-document-ids="insurance.editedByAic ? (insurance.documentIds || []).slice() : []"
              @version-added="versionAdded"
              @document-deleted="deleteDocument" />
          </div>
        </div>
        <div class="row">
          <div class="col-12 mt-3">
            <coozzy-button
              v-if="!insuranceInputReadOnly"
              size="small"
              design="red"
              class="float-right"
              @click="removeClicked">
              {{ $t('message.onBoarding.buildings.insurances.deleteInsurance') }}
            </coozzy-button>
          </div>
        </div>
      </b-collapse>
    </div>
    <b-modal
      :id="'deleteModal'"
      ref="deleteModal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.onBoarding.deleteModals.confirmation')">
      <div class="col">
        <p>{{ $t('message.onBoarding.deleteModals.bodyInsurance') }}</p>
      </div>
      <div class="col">
        <coozzy-button
          size="small"
          class="mb-0 border"
          design="transparent"
          @click="$bvModal.hide('deleteModal')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0"
          design="green"
          @click="removeConfirmed">
          {{ $t('message.generic.delete') }}
        </coozzy-button>
      </div>
    </b-modal>
  </section>
</template>

<script lang="ts">
import Vue from 'vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import AddDocumentModal from '@/properties/components/AddDocumentModal.vue'
import { onboarding } from '@/mixins/onboarding'
import CoozzyFormInputNumeric from '@/framework/components/form/input/CoozzyFormInputNumeric.vue'
import { minValue, required } from 'vuelidate/lib/validators'
import { validation } from '@/mixins/validation'
import CoozzyMultiselect from '@/framework/components/multiselect/CoozzyMultiselect.vue'
import MediaApi from '@/misc/apis/MediaApi'
import DocumentsList from '@/properties/components/DocumentsList.vue'
import { user } from '@/mixins/user'
import { routeChecks } from '@/mixins/routeChecks'
import {
  InsuranceInsurance,
  InsuranceInsuranceBenefit,
  InsuranceInsuranceType, LandingPageEnum,
  MediadocumentDocument,
  TypeDate
} from '@/types/api'
import AicApi from '@/misc/apis/AicApi'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import CoozzyExternalLinkIcon from '@/framework/components/icons/CoozzyExternalLinkIcon.vue'
import { isInsuranceActiveFromDateStrings, typeDateToInputString } from '@/misc/utils/insuranceUtils'

interface InsuranceDateState {
  startDate: string | null
  endDate: string | null
  earliestTerminationDate: string | null
  premiumPaidUntil: string | null
}

interface BenefitOption {
  label: string
  value: InsuranceInsuranceBenefit
}

function insuranceStart(value: string): boolean {
  if (this.insuranceDate.endDate && (this.toTimestamp(value) > this.toTimestamp(this.insuranceDate.endDate))) {
    return false
  }
  return true
}

function insuranceNotice(value: string): boolean {
  if (
    (this.insuranceDate.startDate && (this.toTimestamp(value) < this.toTimestamp(this.insuranceDate.startDate))) ||
    (this.insuranceDate.endDate && (this.toTimestamp(value) > this.toTimestamp(this.insuranceDate.endDate)))
  ) {
    return false
  }
  return true
}

export default Vue.extend({
  name: 'BuildingInsurance',
  components: {
    CoozzyExternalLinkIcon,
    CoozzySpinner,
    DocumentsList,
    CoozzyFormInputNumeric,
    CoozzyMultiselect,
    CoozzyFormSelect,
    CoozzyFormInput,
    CoozzyButton,
    AddDocumentModal
  },
  mixins: [onboarding, validation, user, routeChecks],
  props: {
    index: {
      type: Number,
      default: 0
    },
    insurance: {
      type: Object as () => InsuranceInsurance,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    expandByDefault: {
      type: Boolean,
      default: true
    },
    target: {
      type: String,
      default: 'building'
    },
    addNewDocument: {
      type: Boolean,
      default: false
    },
    syncedToAic: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      insuranceTypeOptions: [
        InsuranceInsuranceType.INSURANCE_TYPE_BUILDING_PROPERTY_LIABILITY,
        InsuranceInsuranceType.INSURANCE_TYPE_LIABILITY,
        InsuranceInsuranceType.INSURANCE_TYPE_BUILDING_PROPERTY_INSURANCE,
        InsuranceInsuranceType.INSURANCE_TYPE_LEGAL_EXPENSES,
        InsuranceInsuranceType.INSURANCE_TYPE_CONSTRUCTION_LIABILITY,
        InsuranceInsuranceType.INSURANCE_TYPE_CANTONAL_BUILDING_INSURANCE,
        InsuranceInsuranceType.INSURANCE_TYPE_BUILDERS_LIABILITY_INSURANCE,
        InsuranceInsuranceType.INSURANCE_TYPE_CONSTRUCTION_INSURANCE,
        InsuranceInsuranceType.INSURANCE_TYPE_OTHER
      ] as InsuranceInsuranceType[],
      availableBenefits: [
        InsuranceInsuranceBenefit.INSURANCE_BENEFIT_LIABILITY,
        InsuranceInsuranceBenefit.INSURANCE_BENEFIT_WATER,
        InsuranceInsuranceBenefit.INSURANCE_BENEFIT_LOSS_OF_RENTAL_INCOME,
        InsuranceInsuranceBenefit.INSURANCE_BENEFIT_BURGLARY_ROBBERY,
        InsuranceInsuranceBenefit.INSURANCE_BENEFIT_GLASS_BREAKAGE,
        InsuranceInsuranceBenefit.INSURANCE_BENEFIT_EXTENDED_COVERAGE,
        InsuranceInsuranceBenefit.INSURANCE_BENEFIT_BUILDING_SERVICES_FAILURE,
        InsuranceInsuranceBenefit.INSURANCE_BENEFIT_CONVERSIONS_AND_RENOVATIONS,
        InsuranceInsuranceBenefit.INSURANCE_BENEFIT_NATURAL_HAZARDS,
        InsuranceInsuranceBenefit.INSURANCE_BENEFIT_ACCIDENT_AND_SICKNESS
      ] as InsuranceInsuranceBenefit[],
      availableInsuranceCompanies: [
        'Aargauische Gebäudeversicherung',
        'Allianz Suisse Versicherungs-Gesellschaft AG',
        'Appenzeller Versicherungen',
        'Assekuranz Appenzell Ausserrhoden',
        'Assista Rechtsschutz AG',
        'AXA Versicherungen (Schweiz) AG',
        'AXA XL Schweiz',
        'Baloise Versicherung AG',
        'Basellandschaftliche Gebäudeversicherung',
        'Beazley Insurance Designated Activity Company, Dublin, Schweizer Zweigniederlassung Zürich',
        'CAP Rechtsschutz‑Versicherungsgesellschaft AG',
        'Chubb Versicherungen (Schweiz) AG',
        'Coop Rechtsschutz AG',
        'CSS Versicherung AG',
        'Dextra Rechtsschutz AG',
        'Elvia Versicherung AG',
        'emmental versicherung Genossenschaft',
        'Etablissement cantonal d\'assurance des bâtiments, Fribourg',
        'Etablissement cantonal d\'assurance immobilière et de prévention',
        'Etablissement cantonal d\'assurance et de prévention',
        'Firstcaution SA',
        'Gebäudeversicherung  Basel Stadt',
        'Gebäudeversicherung Bern',
        'Gebäudeversicherung des Kanton Schaffhausen',
        'Gebäudeversicherung des Kanton St. Gallen',
        'Gebäudeversicherung Graubünden',
        'Gebäudeversicherung Kanton Zürich GVZ',
        'Gebäudeversicherung Luzern',
        'Gebäudeversicherung Thurgau',
        'Gebäudeversicherung Zug',
        'Generali Versicherungen AG (Schweiz)',
        'Glarner Sach',
        'GO Caution',
        'Groupe Mutuel Assurance SA',
        'GVB Privatversicherungen AG',
        'HDI Global SE (Schweiz)',
        'Helvetia Schweizerische Versicherungsgesellschaft AG Schweiz',
        'Helsana Versicherungen AG',
        'Liberty Mutual Insurance Europe SE (Schweiz)',
        'Lloyd\'s, London, Zweigniederlassung Zürich',
        'Markel Insurance SE (CH)',
        'Nidwaldersachversicherung',
        'Protekta Rechtsschutz‑Versicherung AG',
        'Schweizerische Mobiliar Versicherungsgesellschaft AG',
        'Simpego Versicherungen AG',
        'SmartCaution SA',
        'Smile.Direct Insurance AG',
        'Solothurnische Gebäudeversicherung SGV',
        'Sompo',
        'Starr International Insurance (Switzerland) AG',
        'Stena Insurance AG',
        'Sten Met Insurance AG',
        'Swiss Life AG',
        'SwissCaution SA',
        'UNIQA Österreich Versicherungen AG, Wien, Zweigniederlassung Zürich',
        'VAUDOISE GENERALE, Compagnie d\'Assurances SA',
        'Visana Allgemeine Versicherungen AG',
        'Zürich Versicherungs-Gesellschaft AG'
      ] as string[],
      insuranceDate: {
        startDate: null,
        endDate: null,
        earliestTerminationDate: null,
        premiumPaidUntil: null
      } as InsuranceDateState,
      documentList: [] as MediadocumentDocument[],
      aicLoading: false
    }
  },
  computed: {
    availableTypes(): InsuranceInsuranceType[] {
      const translationKey = 'message.onBoarding.buildings.insurances.type.'
      return [...this.insuranceTypeOptions].sort((a, b) =>
        this.$t(translationKey + a).localeCompare(this.$t(translationKey + b), this.$i18n.locale)
      )
    },
    type: {
      get(): InsuranceInsuranceType {
        return this.insurance.type
      },
      set(value: InsuranceInsuranceType) {
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.type = value
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    benefits: {
      get(): BenefitOption[] {
        return this.insurance.benefits
          .filter((con: InsuranceInsuranceBenefit) => con !== InsuranceInsuranceBenefit.INSURANCE_BENEFIT_UNDEFINED)
          .map((conTyp: InsuranceInsuranceBenefit) => ({
            label: this.$t('message.onBoarding.buildings.insurances.benefits.' + conTyp),
            value: conTyp
          }))
      },
      set(value: BenefitOption[]) {
        const conTypValues = value.map((v: BenefitOption) => v.value)
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.benefits = conTypValues
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    notes: {
      get(): string {
        return this.insurance.notes
      },
      set(value: string) {
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.notes = value
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    premiumPaidUntil: {
      get(): TypeDate | null {
        return this.insurance.premiumPaidUntil
      },
      set(value: TypeDate | null) {
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.premiumPaidUntil = value as TypeDate
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    insuranceCompany: {
      get(): string {
        return this.insurance.insuranceCompany
      },
      set(value: string) {
        if (this.isInsuranceCompanyReadOnly) {
          return
        }
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.insuranceCompany = value
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    policyNumber: {
      get(): string {
        return this.insurance.policyNumber
      },
      set(value: string) {
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.policyNumber = value
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    amount: {
      get(): number | null {
        return this.insurance.amount !== -1 ? this.insurance.amount : null
      },
      set(value: number | null) {
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.amount = value
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    yearlyCosts: {
      get(): number | null {
        return this.insurance.yearlyCosts !== -1 ? this.insurance.yearlyCosts : null
      },
      set(value: number | null) {
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.yearlyCosts = value
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    excess: {
      get(): number | null {
        return this.insurance.excess !== -1 ? this.insurance.excess : null
      },
      set(value: number | null) {
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.excess = value
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    indexYear: {
      get(): number | null {
        const value = this.insurance.indexYear
        if (value === -1 || value === null || value === undefined || value < 0) {
          return null
        }
        return value
      },
      set(value: number | null) {
        if (value !== null && value !== undefined && value < 0) {
          value = null
        }
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.indexYear = value
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    indexYearInput: {
      get(): string {
        const value = this.insurance.indexYear
        if (value === -1 || value === null || value === undefined || value < 0) {
          return ''
        }
        return value.toString()
      },
      set(value: string) {
        // Only allow digits, limit to 4 characters
        const digitsOnly = value.replace(/[^0-9]/g, '').substring(0, 4)
        if (digitsOnly === '') {
          this.indexYear = null
        } else {
          const yearValue = parseInt(digitsOnly, 10)
          if (!isNaN(yearValue) && yearValue >= 0) {
            this.indexYear = yearValue
          } else {
            this.indexYear = null
          }
        }
      }
    },
    startDate: {
      get(): TypeDate | null {
        return this.insurance.startDate
      },
      set(value: TypeDate | null) {
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.startDate = value
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    endDate: {
      get(): TypeDate | null {
        return this.insurance.endDate
      },
      set(value: TypeDate | null) {
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.endDate = value
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    earliestTerminationDate: {
      get(): TypeDate | null {
        return this.insurance.earliestTerminationDate
      },
      set(value: TypeDate | null) {
        const newInsurance: InsuranceInsurance = this.insurance
        newInsurance.earliestTerminationDate = value
        this.$store.dispatch('onboarding/updateInsurance', newInsurance)
      }
    },
    documents(): MediadocumentDocument[] {
      if (this.target !== 'property') {
        return this.getFilteredInsuranceDocuments(this.insurance.documentIds)
          .sort((a: MediadocumentDocument, b: MediadocumentDocument) => (a.name > b.name ? 1 : -1))
      } else {
        return [...this.documentList].sort((a: MediadocumentDocument, b: MediadocumentDocument) => (a.name > b.name ? 1 : -1))
      }
    },
    translatedBenefits(): BenefitOption[] {
      return this.availableBenefits.map((conTyp: InsuranceInsuranceBenefit) => ({
        label: this.$t('message.onBoarding.buildings.insurances.benefits.' + conTyp),
        value: conTyp
      }))
    },
    getBenefits(): string[] {
      return this.insurance.benefits
        .map((conTyp: InsuranceInsuranceBenefit) =>
          this.$t('message.onBoarding.buildings.insurances.benefits.' + conTyp)
        )
        .join(', ')
    },
    insuranceCompanyOptions(): string[] {
      // Add all predefined companies, and if the current company is not present, include it as well
      const options: string[] = [...this.availableInsuranceCompanies]
      if (this.insuranceCompany && !this.availableInsuranceCompanies.includes(this.insuranceCompany)) {
        options.unshift(this.insuranceCompany)
      }
      return options
    },
    isCantonalBuildingInsurance(): boolean {
      return this.type === InsuranceInsuranceType.INSURANCE_TYPE_CANTONAL_BUILDING_INSURANCE
    },
    isNewInsurance(): boolean {
      return !this.insurance.id
    },
    isInsuranceCompanyReadOnly(): boolean {
      return !!(this.insurance.id && this.insurance.insuranceCompany)
    },
    insuranceInputReadOnly(): boolean {
      return this.readOnly || !!this.insurance.editedByAic
    },
    insuranceListTitle(): string {
      let title = this.$t('message.onBoarding.buildings.insurances.type.' + this.type) as string
      if (this.insuranceCompany) {
        title += ': ' + this.insuranceCompany
      }
      const rest: string[] = []
      if (this.policyNumber) {
        rest.push(this.policyNumber)
      }
      rest.push(this.insuranceDate.startDate
        ? this.$options.filters.formatDate(this.insuranceDate.startDate)
        : 'n/a')
      rest.push(this.insuranceDate.endDate
        ? this.$options.filters.formatDate(this.insuranceDate.endDate)
        : 'n/a')
      rest.push(this.$t(
        isInsuranceActiveFromDateStrings(this.insuranceDate.startDate, this.insuranceDate.endDate)
          ? 'message.onBoarding.buildings.insurances.active'
          : 'message.onBoarding.buildings.insurances.inactive'
      ) as string)
      return title + ' - ' + rest.join(' - ')
    },
    insurancePropDateKey(): string {
      return [
        typeDateToInputString(this.startDate),
        typeDateToInputString(this.endDate),
        typeDateToInputString(this.earliestTerminationDate),
        typeDateToInputString(this.premiumPaidUntil)
      ].join('|')
    }
  },
  watch: {
    insurancePropDateKey() {
      this.syncInsuranceDateFromProp()
    },
    insurance: {
      deep: true,
      handler() {
        this.$emit('change-insurance')
      }
    }
  },
  mounted() {
    this.syncInsuranceDateFromProp()

    if (this.target === 'property' && this.insurance.documentIds.length > 0) {
      MediaApi.listDocuments(this.insurance.documentIds)
        .then((response: { documents: MediadocumentDocument[] }) => {
          this.documentList = response.documents
        })
    }
    this.$nextTick(() => {
      if (!this.readOnly && this.addNewDocument) {
        this.addDocumentClicked('insurances' + this.index)
      }
    })
  },
  beforeDestroy() {
    this.$root.$off('bv::collapse::state')
  },
  methods: {
    goToEditMode() {
      let routeLink = null
      localStorage.setItem('insuranceIdNewDocument', this.insurance.id)
      if (this.$route.name?.includes('PropertyDetailsView')) {
        routeLink = {
          name: this.moduleRoutePrefix + 'PropertyEditingView',
          params: { id: this.$route.params.id },
          query: { view: 'property' }
        }
      } else if (this.$route.name?.includes('BuildingDetailsView')) {
        routeLink = {
          name: this.moduleRoutePrefix + 'OnboardingView',
          params: { id: this.$route.params.id },
          query: { view: 'building' }
        }
      }
      this.$router.push(routeLink)
    },
    addDocumentClicked(suffix = '') {
      localStorage.removeItem('insuranceIdNewDocument')
      this.$bvModal.show('add-document-modal' + suffix)
    },
    syncInsuranceDateFromProp() {
      this.insuranceDate.startDate = typeDateToInputString(this.startDate)
      this.insuranceDate.endDate = typeDateToInputString(this.endDate)
      this.insuranceDate.earliestTerminationDate = typeDateToInputString(this.earliestTerminationDate)
      this.insuranceDate.premiumPaidUntil = typeDateToInputString(this.premiumPaidUntil)
    },
    dateToTimestamp() {
      if (this.insuranceDate.startDate && this.insuranceDate.startDate !== '') {
        const startDate = new Date(this.insuranceDate.startDate)
        this.startDate = {
          year: startDate.getFullYear(),
          month: startDate.getMonth() + 1,
          day: startDate.getDate()
        }
      } else {
        this.startDate = null
      }
      if (this.insuranceDate.endDate && this.insuranceDate.endDate !== '') {
        const endDate = new Date(this.insuranceDate.endDate)
        this.endDate = {
          year: endDate.getFullYear(),
          month: endDate.getMonth() + 1,
          day: endDate.getDate()
        }
      } else {
        this.endDate = null
      }
      if (this.insuranceDate.premiumPaidUntil && this.insuranceDate.premiumPaidUntil !== '') {
        const date = new Date(this.insuranceDate.premiumPaidUntil)
        this.premiumPaidUntil = {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate()
        }
      } else {
        this.premiumPaidUntil = null
      }
      if (this.insuranceDate.earliestTerminationDate && this.insuranceDate.earliestTerminationDate !== '') {
        const earliestTerminationDate = new Date(this.insuranceDate.earliestTerminationDate)
        this.earliestTerminationDate = {
          year: earliestTerminationDate.getFullYear(),
          month: earliestTerminationDate.getMonth() + 1,
          day: earliestTerminationDate.getDate()
        }
      } else {
        this.earliestTerminationDate = null
      }
    },
    documentCreated(document: MediadocumentDocument) {
      // Update insurance in vuex store with new documentId
      const insurance: InsuranceInsurance = this.insurance
      insurance.documentIds.push(document.id)
      this.$store.dispatch('onboarding/updateInsurance', insurance)

      // Add whole new document to vuex store to display it
      if (this.target !== 'property') {
        this.insuranceDocuments.push(document)
      } else {
        this.documentList.push(document)
      }
    },
    removeClicked() {
      this.$refs.deleteModal.show()
    },
    removeConfirmed() {
      this.removeInsurance(this.insurance)
      this.$refs.deleteModal.hide()
      this.$emit('removeConfirmed')
    },
    toTimestamp(strDate: string): number {
      return Date.parse(strDate) / 1000
    },
    versionAdded(updatedDocument: MediadocumentDocument) {
      this.insuranceDocuments.forEach((doc: MediadocumentDocument, index: number) => {
        if (doc.id === updatedDocument.id) {
          this.insuranceDocuments.splice(index, 1, updatedDocument)
        }
      })
      const index = this.documentList.findIndex((d: MediadocumentDocument) => d.id === updatedDocument.id)
      if (index !== -1) {
        this.$set(this.documentList, index, updatedDocument)
      }
    },
    deleteDocument(document: MediadocumentDocument) {
      this.insurance.documentIds.forEach((docId: string, index: number) => {
        if (docId === document.id) {
          this.insurance.documentIds.splice(index, 1)
        }
      })
      if (this.target !== 'property') {
        const indexToDelete = this.insuranceDocuments.findIndex(
          (d: MediadocumentDocument) => d.id === document.id
        )
        if (indexToDelete !== -1) {
          this.$delete(this.insuranceDocuments, indexToDelete)
        }
      } else {
        const indexToDelete = this.documentList.findIndex(
          (d: MediadocumentDocument) => d.id === document.id
        )
        if (indexToDelete !== -1) {
          this.$delete(this.documentList, indexToDelete)
        }
      }
      this.$store.dispatch('onboarding/updateInsurance', this.insurance)
      this.activateChanged()
    },
    isInvalid(): boolean {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.scrollToError()
      }
      if (this.$v.insuranceDate.startDate.$dirty && this.$v.insuranceDate.startDate.$error) {
        Vue.toasted.show(this.$t('message.savingErrors.insuranceStartDate'), { type: 'error' })
      }
      if (this.$v.insuranceDate.earliestTerminationDate.$dirty && this.$v.insuranceDate.earliestTerminationDate.$error) {
        Vue.toasted.show(this.$t('message.savingErrors.insuranceEarliestTerminationDate'), { type: 'error' })
      }
      return this.$v.$invalid
    },
    isCollapseOpen() {
      return this.$refs.collapse.visible
    },
    showCoverageInAic() {
      this.aicLoading = true
      AicApi.getSsoUrl(LandingPageEnum.AIC_LANDING_PAGE_INSURANCE, this.insurance.id)
        .then(response => {
          window.open(response.url, '_blank')
        })
        .catch(() => {
          Vue.toasted.show(this.$t('message.loadingErrors.aicSsoUrl'), { type: 'error' })
        })
        .finally(() => {
          this.aicLoading = false
        })
    }
  },
  validations() {
    let validations = {
      type: {},
      insuranceCompany: {},
      yearlyCosts: {},
      excess: {},
      insuranceDate: {
        startDate: {},
        earliestTerminationDate: {}
      },
      amount: {},
      indexYear: {}
    }

    if (this.insurance.editedByAic) {
      return validations
    }

    validations = {
      type: {
        required
      },
      insuranceCompany: {
        required
      },
      yearlyCosts: {
        minValue: minValue(0)
      },
      excess: {
        minValue: minValue(0)
      },
      insuranceDate: {
        startDate: {
          insuranceStart
        },
        earliestTerminationDate: {
          insuranceNotice
        }
      },
      amount: {},
      indexYear: {}
    }

    // Require amount only for new cantonal building insurance entries (COOZ-16033)
    if (this.isCantonalBuildingInsurance && this.isNewInsurance) {
      validations.amount = { required, minValue: minValue(0) }
    } else {
      validations.amount = { minValue: minValue(0) }
    }
    if (this.isCantonalBuildingInsurance) {
      validations.indexYear = {
        validYear: (value: number | null): boolean => {
          if (!value && value !== 0) return true
          const yearString = value.toString()
          if (yearString.length !== 4) return false
          if (!/^\d{4}$/.test(yearString)) return false
          const year = parseInt(yearString, 10)
          return !isNaN(year) && year >= 1900 && year <= 2100
        }
      }
    }

    return validations
  }
})
</script>

<style lang="scss" scoped>

</style>
